import React, { useState } from 'react';
import { toast } from 'react-toastify';

/**
 * DEV-ONLY banner that lists the well-known Hardhat deterministic accounts so
 * developers can copy/paste private keys into MetaMask's "Import Account" flow.
 *
 * MetaMask intentionally does NOT expose wallet_importRawKey, so programmatic
 * key import is impossible. This banner is the lowest-friction alternative.
 *
 * These keys are PUBLIC KNOWLEDGE (derived from the default Hardhat mnemonic
 * "test test test test test test test test test test test junk"). Never use
 * them with real funds.
 *
 * Rendered only when NODE_ENV === 'development'.
 */

// Hardhat's deterministic account list (accounts #0–#4 are sufficient for dev).
const HARDHAT_ACCOUNTS = [
  {
    index: 0,
    address: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
    privateKey: '0xac0974bec39a17e36ba4a6b4d238ff944bacb478cbed5efcae784d7bf4f2ff80',
  },
  {
    index: 1,
    address: '0x70997970C51812dc3A010C7d01b50e0d17dc79C8',
    privateKey: '0x59c6995e998f97a5a0044966f0945389dc9e86dae88c7a8412f4603b6b78690d',
  },
  {
    index: 2,
    address: '0x3C44CdDdB6a900fa2b585dd299e03d12FA4293BC',
    privateKey: '0x5de4111afa1a4b94908f83103eb1f1706367c2e68ca870fc3fb9a804cdab365a',
  },
  {
    index: 3,
    address: '0x90F79bf6EB2c4f870365E785982E1f101E93b906',
    privateKey: '0x7c852118294e51e653712a81e05800f419141751be58f605c371e15141b007a6',
  },
  {
    index: 4,
    address: '0x15d34AAf54267DB7D7c367839AAf71A00a2C6A65',
    privateKey: '0x47e179ec197488593b187f80a00eb0da91f1b9d0b13f8733639f19c30a34926a',
  },
];

const copyBtnBase = {
  padding: '0.2rem 0.55rem',
  borderRadius: '0.25rem',
  border: '1px solid transparent',
  cursor: 'pointer',
  fontSize: '0.72rem',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
};

export const DevAccountsBanner = () => {
  // Expand by default on first visit so keys are immediately visible.
  // Collapsed preference is remembered in localStorage.
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem('dev-accounts-banner-collapsed') !== 'true';
    } catch {
      return true;
    }
  });

  if (process.env.NODE_ENV !== 'development') return null;

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Clipboard unavailable — copy manually');
    }
  };

  const toggle = () =>
    setExpanded((v) => {
      const next = !v;
      try {
        localStorage.setItem('dev-accounts-banner-collapsed', String(!next));
      } catch { /* storage unavailable — ignore */ }
      return next;
    });

  return (
    <div
      role="region"
      aria-label="Development accounts panel"
      style={{
        border: '2px solid #f59e0b',
        borderRadius: '0.5rem',
        background: '#fffbeb',
        padding: '0.75rem 1rem',
        marginBottom: '1rem',
        fontSize: '0.84rem',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={toggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle()}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls="dev-accounts-list"
      >
        <strong style={{ color: '#92400e' }}>
          DEV — Hardhat Accounts (hidden in production)
        </strong>
        <span style={{ color: '#92400e', fontSize: '0.8rem' }}>
          {expanded ? '▲ Hide' : '▼ Show accounts & keys'}
        </span>
      </div>

      {/* Account list */}
      {expanded && (
        <div id="dev-accounts-list" style={{ marginTop: '0.75rem' }}>
          <p style={{ color: '#78350f', marginBottom: '0.6rem', lineHeight: 1.5 }}>
            MetaMask blocks programmatic key import for security. To use a Hardhat
            account: open MetaMask → click account icon → <strong>Import account</strong> →
            paste the private key below.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {HARDHAT_ACCOUNTS.map((acc) => (
              <div
                key={acc.index}
                style={{
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  fontFamily: 'monospace',
                  fontSize: '0.77rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.3rem',
                }}
              >
                <span style={{ fontWeight: 600, color: '#451a03' }}>
                  Account #{acc.index} · 10 000 ETH (dev)
                </span>

                {/* Address row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <span style={{ color: '#6b7280', minWidth: '2.5rem' }}>Addr</span>
                  <span style={{ flex: 1, wordBreak: 'break-all' }}>{acc.address}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(acc.address, 'Address')}
                    style={{ ...copyBtnBase, background: '#e5e7eb', borderColor: 'transparent' }}
                  >
                    Copy
                  </button>
                </div>

                {/* Private key row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <span style={{ color: '#6b7280', minWidth: '2.5rem' }}>Key</span>
                  <span style={{ flex: 1, wordBreak: 'break-all', color: '#b91c1c' }}>
                    {acc.privateKey}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(acc.privateKey, 'Private key')}
                    style={{ ...copyBtnBase, background: '#fecaca', borderColor: 'transparent' }}
                  >
                    Copy key
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p style={{ color: '#92400e', marginTop: '0.5rem', fontSize: '0.77rem' }}>
            These accounts are derived from the public Hardhat mnemonic. Never use them with real ETH.
          </p>
        </div>
      )}
    </div>
  );
};
